import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Slide,
  TextField,
  Toolbar,
  Typography,
  ThemeProvider,
  createTheme,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import HomeworkInfo from '../common/HomeworkInfo';
import HomeworkUploads from '../common/HomeworkUploads';
import { db } from '../service/Firebase-config';
import { addDoc, collection, onSnapshot, orderBy, query, doc, updateDoc } from 'firebase/firestore';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const defaultTheme = createTheme();

export default function DialogEditHomework() {
  const { selectedUser } = useGlobalContext();
  const { selectedHw, setSelectedHw, openEditHw, setOpenEditHw } = useHomeworkContext();
  const [comments, setComments] = useState([]);
  const [commentMessage, setCommentMessage] = useState('');
  const [isNewCommentLoading, setIsNewCommentLoading] = useState(false);
  const [journal, setJournal] = useState('');
  const [topic, setTopic] = useState('');
  const [isJournalLoading, setIsJournalLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (selectedUser && selectedHw) {
      const collectionRef = collection(db, `students/${selectedUser.uid}/homeworks/${selectedHw.id}/comments`);
      const orderedQuery = query(collectionRef, orderBy('createdDate'));
      const unsubscribe = onSnapshot(orderedQuery, (snapshot) => {
        setComments(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      });

      setJournal(selectedHw?.journal || ''); // Update journal when selectedHw changes
      setTopic(selectedHw?.topic || '');
      return () => unsubscribe();
    }
  }, [selectedUser, selectedHw]);


  const sendJournal = async () => {
    setIsJournalLoading(true);
    const homeworkRef = doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id);
    await updateDoc(homeworkRef, { journal, topic }).then(() => {
      setIsJournalLoading(false);
      setSnackbarOpen(true); // Open the snackbar when the journal is saved
    });
  };

  const sendCommentMessage = async () => {
    setIsNewCommentLoading(true);
    const commentData = {
      createdDate: new Date(),
      comment: commentMessage,
      fromWhom: 'teacher',
    };
    const dbCollection = collection(db, `students/${selectedUser.uid}/homeworks/${selectedHw.id}/comments`);
    await addDoc(dbCollection, commentData);
    const homeworkRef = doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id);
    await updateDoc(homeworkRef, { isNewComment: true });

    setCommentMessage('');
    setIsNewCommentLoading(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const clear = () => {
    setSelectedHw(null);
    setOpenEditHw(false); 
    setCommentMessage('');
  }
  return (
    <Dialog fullScreen open={openEditHw} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => clear()} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Close
          </Typography>
        </Toolbar>
      </AppBar>
      <ThemeProvider theme={defaultTheme}>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={9}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  <HomeworkInfo />
                </Paper>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', mt: 3 }}>
                  <HomeworkUploads />
                </Paper>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', mt: 3 }}>
                <TextField
                    margin="dense"
                    id="topic"
                    name="topic"
                    label="Topic (Teacher Only)"
                    multiline
                    rows={2}
                    fullWidth
                    variant="outlined"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  />
                  <TextField
                    margin="dense"
                    id="journal"
                    name="journal"
                    label="Journal (Teacher Only)"
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    value={journal}
                    onChange={(e) => setJournal(e.target.value)}
                  />
                  <TextField
                    margin="dense"
                    id="adminComment"
                    name="adminComment"
                    label="Comment (Admin Only)"
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    value={selectedHw?.adminComment || ''}
                    disabled
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button variant="contained" color="primary" onClick={sendJournal} disabled={isJournalLoading}>
                      {isJournalLoading ? 'Saving...' : 'Save Journal'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={12} lg={3}>
                <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '500px', mb: 4 }}>
                  <Typography variant="h6" sx={{ p: 2, borderBottom: '1px solid lightgrey' }}>
                    Message
                  </Typography>
                  <Paper sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
                    {comments &&
                      comments.map((data) => (
                        <Box
                          key={data.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: data.fromWhom === 'teacher' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <Typography
                            sx={{
                              backgroundColor: data.fromWhom === 'teacher' ? '#3f51b5' : '#e0e0e0',
                              color: data.fromWhom === 'teacher' ? '#fafafa' : '#000000',
                              display: 'inline-block',
                              p: '5px',
                              m: '5px',
                              maxWidth: '80%',
                              borderRadius: '10px',
                              wordBreak: 'break-word',
                            }}
                          >
                            {data.comment}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              mx: 1,
                              color: 'text.secondary',
                            }}
                          >
                            {data.createdDate?.toDate().toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }).replace('AM','am').replace('PM','pm')}
                          </Typography>
                        </Box>
                      ))}

                    {isNewCommentLoading && <Typography>Loading ...</Typography>}
                  </Paper>
                  <Box m={1}>
                    <TextField
                      placeholder="Send Message"
                      sx={{ width: '100%' }}
                      value={commentMessage}
                      onChange={(e) => setCommentMessage(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" color="primary"  disabled={commentMessage.trim().length === 0} onClick={sendCommentMessage}>
                              <SendIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </ThemeProvider>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Journal Saved!
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
